import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import {
  GitBranchPlus, Sparkles, Clapperboard, Globe2, GalleryHorizontalEnd,
  ShieldCheck, ArrowRight, LogIn, Lock, Users, Heart, Waypoints, Menu, X,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1609260840244-22b52bfc6664?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  flatlay: "https://images.pexels.com/photos/17951106/pexels-photo-17951106.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  portraits: "https://images.pexels.com/photos/16865233/pexels-photo-16865233.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  reading: "https://images.pexels.com/photos/6158677/pexels-photo-6158677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const Eyebrow = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#007F6B]">{children}</span>
);

function FeatureCard({ icon: Icon, title, desc, span = "", tone = "light", img, testid }) {
  const dark = tone === "dark";
  return (
    <div
      data-testid={testid}
      className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${span} ${
        dark ? "border-[#082D4A] bg-[#082D4A] text-white" : "border-[#D8E0E8] bg-white"
      }`}
    >
      {img && (
        <img src={img} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20 transition-opacity duration-500 group-hover:opacity-30" />
      )}
      <div className="relative">
        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${dark ? "bg-[#D4A017] text-[#082D4A]" : "bg-[#E8EEF3] text-[#082D4A]"}`}>
          <Icon size={22} />
        </div>
        <h3 className={`mt-5 font-serif text-xl font-semibold sm:text-2xl ${dark ? "text-white" : "text-[#082D4A]"}`}>{title}</h3>
        <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-white/80" : "text-[#475569]"}`}>{desc}</p>
      </div>
    </div>
  );
}

export function LandingPage({ onSignUp, onSignIn, onDonate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const go = (id) => { scrollTo(id); setMenuOpen(false); };

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const sections = [
    { id: "features", label: "Features" },
    { id: "how", label: "How it works" },
    { id: "privacy", label: "Privacy" },
  ];

  return (
    <div className="min-h-screen overflow-y-auto overflow-x-hidden bg-[#F4F6F8]" data-testid="landing-page">
      {/* Nav */}
      <header data-testid="nav-header" className="sticky top-0 z-40 border-b border-[#D8E0E8] bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-3">
          <div className="flex min-w-0 items-center gap-3" data-testid="nav-logo">
            <img src="/logo-mark.png" alt="Where Am I From" className="h-10 w-10 shrink-0 object-contain sm:h-[52px] sm:w-[52px]" />
            <span className="truncate font-brand text-lg tracking-tight text-[#082D4A] sm:text-2xl">Where Am i From<span className="text-[#8A6A0C]">.net</span></span>
          </div>
          <nav className="hidden items-center gap-7 text-sm font-medium text-[#475569] lg:flex">
            {sections.map((s) => (
              <button key={s.id} onClick={() => scrollTo(s.id)} data-testid={`nav-link-${s.id}`} className="transition-colors hover:text-[#082D4A]">{s.label}</button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button onClick={onSignIn} data-testid="nav-signin-btn" variant="ghost" className="rounded-full text-[#082D4A] hover:bg-[#E8EEF3]">
              <LogIn size={16} className="mr-1.5" /> Sign in
            </Button>
            <Button onClick={onSignUp} data-testid="nav-signup-btn" className="rounded-full bg-[#D4A017] font-semibold text-[#082D4A] hover:bg-[#B0850F]">
              <span className="hidden sm:inline">Create your family tree</span>
              <span className="sm:hidden">Get started</span>
            </Button>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-panel"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3] lg:hidden"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div id="mobile-nav-panel" data-testid="mobile-nav-panel" className="border-t border-[#D8E0E8] bg-white lg:hidden">
            <nav className="mx-auto flex max-w-6xl flex-col px-5 py-1">
              {sections.map((s) => (
                <button key={s.id} onClick={() => go(s.id)} data-testid={`mobile-nav-${s.id}`} className="border-b border-[#EEF2F6] py-3.5 text-left text-base font-medium text-[#082D4A] last:border-0 hover:text-[#007F6B]">
                  {s.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 lg:grid-cols-[1.15fr_0.85fr] lg:py-24">
        <div>
          <Eyebrow>Private family trees & AI storytelling</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-[#082D4A] sm:text-5xl lg:text-6xl">
            Your family’s story,<br />in a living private tree.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#475569]">
            Turn scattered photos, names, and oral memories into an interactive family tree —
            then bring it to life with a narrated video, a 3D migration globe, and a timeline reel.
            Every family gets its own private space.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button onClick={onSignUp} data-testid="hero-primary-cta" className="h-12 rounded-full bg-[#082D4A] px-7 text-base text-white transition-transform hover:-translate-y-0.5 hover:bg-[#08223A]">
              Create your family tree <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button onClick={() => scrollTo("features")} data-testid="hero-secondary-cta" variant="outline" className="h-12 rounded-full border-[#D8E0E8] bg-white px-7 text-base text-[#082D4A] hover:bg-[#E8EEF3]">
              See what you can build
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-[#475569]">
            <span className="flex items-center gap-1.5"><Lock size={13} className="text-[#007F6B]" /> 100% private by default</span>
            <span className="flex items-center gap-1.5"><Users size={13} className="text-[#007F6B]" /> Invite your relatives</span>
            <span className="flex items-center gap-1.5"><Heart size={13} className="text-[#007F6B]" /> No ads, ever</span>
          </div>
        </div>

        <div className="relative">
          <div className="overflow-hidden rounded-[2rem] border border-[#D8E0E8] shadow-2xl">
            <img src={IMG.hero} alt="A family across generations" className="h-[420px] w-full object-cover sm:h-[480px]" />
          </div>
          <div className="absolute -left-4 bottom-8 hidden rounded-2xl border border-[#D8E0E8] bg-white/95 p-4 shadow-xl backdrop-blur sm:block">
            <div className="flex items-center gap-2 text-[#082D4A]"><Waypoints size={18} className="text-[#D4A017]" /><span className="font-serif text-sm font-semibold">4 generations linked</span></div>
            <p className="mt-1 text-xs text-[#64748B]">Grandparents → parents → you</p>
          </div>
          <div className="absolute -right-3 top-8 hidden rounded-2xl border border-[#D8E0E8] bg-white/95 p-3 shadow-xl backdrop-blur sm:flex sm:items-center sm:gap-2">
            <Clapperboard size={18} className="text-[#007F6B]" /><span className="text-xs font-medium text-[#082D4A]">Narrated video ready</span>
          </div>
        </div>
      </section>

      {/* Brand strip */}
      <div className="border-y border-[#0e3a5c] bg-[#082D4A]" data-testid="brand-strip">
        <div className="brand-marquee-mask mx-auto max-w-6xl overflow-hidden px-5 py-3.5">
          <div className="brand-marquee-track flex w-max items-center whitespace-nowrap">
            {Array.from({ length: 2 }).map((_, r) => (
              <div key={r} className="flex items-center">
                {["People", "Places", "Generations", "One Story", "Same Roots", "A Brighter Tomorrow"].map((w) => (
                  <span key={w} className="flex items-center text-xs font-semibold uppercase tracking-[0.28em] text-white/85">
                    {w}<span className="mx-6 text-[#D4A017]">•</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust stats */}
      <section className="border-y border-[#D8E0E8] bg-white">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-5 py-10 sm:grid-cols-3">
          {[
            { v: "One tree per family", l: "Fully private & isolated" },
            { v: "AI-assisted", l: "Paste notes, we fill the tree" },
            { v: "Globe · Video · Reel", l: "Your memories, brought to life" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <div className="font-serif text-2xl font-bold text-[#082D4A]">{s.v}</div>
              <div className="mt-1 text-sm text-[#475569]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Bento features */}
      <section id="features" className="mx-auto max-w-6xl px-5 py-20">
        <div className="max-w-2xl">
          <Eyebrow>Everything in one place</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-[#082D4A] sm:text-4xl">More than a chart — a home for your heritage</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          <FeatureCard testid="feature-tree-canvas" span="md:col-span-2" icon={GitBranchPlus} title="Interactive family tree canvas" desc="Drag, connect, and arrange relatives across generations. Spouses sit side by side, children below — with photos on every card." img={IMG.portraits} />
          <FeatureCard testid="feature-ai-entry" icon={Sparkles} title="AI smart data entry" desc="Paste a paragraph or an old letter and let AI extract names, dates, places, and relationships for you." />
          <FeatureCard testid="feature-video-slideshow" icon={Clapperboard} title="Narrated story video" desc="Generate a warm, narrated slideshow of a person or a whole branch — play it in-app or export an MP4." />
          <FeatureCard testid="feature-3d-globe" span="md:col-span-2" tone="dark" icon={Globe2} title="3D migration globe" desc="Watch your family journey across the world — origin-to-home arcs animate on an interactive globe." img={IMG.flatlay} />
          <FeatureCard testid="feature-photo-timeline" icon={GalleryHorizontalEnd} title="Photo wall & timeline reel" desc="A beautiful collage of every memory, plus an auto-playing timeline of your family’s milestones." />
          <FeatureCard testid="feature-private-spaces" span="md:col-span-3" icon={ShieldCheck} title="Private family spaces with invite links" desc="Your tree is completely private to your household. Invite relatives with one-time links, or flip on a read-only public link when you're ready to share." />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-[#D8E0E8] bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="max-w-2xl">
            <Eyebrow>Simple from the start</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-[#082D4A] sm:text-4xl">Up and running in three steps</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Create your private space", d: "Sign up in seconds with your name and email. Your family tree is yours alone — invite relatives whenever you like." },
              { n: "02", t: "Add relatives, the easy way", d: "Type names or paste notes and photos. AI helps fill in birth dates, places, and how everyone connects." },
              { n: "03", t: "Bring the story to life", d: "Generate a narrated video, spin the migration globe, and share a read-only link with the whole family." },
            ].map((s) => (
              <div key={s.n} className="rounded-3xl border border-[#D8E0E8] bg-[#F4F6F8] p-7">
                <div className="font-serif text-4xl font-bold text-[#8A6A0C]">{s.n}</div>
                <h3 className="mt-3 font-serif text-xl font-semibold text-[#082D4A]">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section id="privacy" className="mx-auto max-w-6xl px-5 py-20">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="order-2 overflow-hidden rounded-[2rem] border border-[#D8E0E8] shadow-xl lg:order-1">
            <img src={IMG.reading} alt="Grandparent sharing a story" className="h-[380px] w-full object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <Eyebrow>Yours, and yours alone</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-[#082D4A] sm:text-4xl">Private by design, shared on your terms</h2>
            <ul className="mt-6 space-y-4">
              {[
                ["Every family is isolated", "New families get a fresh, empty tree with zero access to anyone else's."],
                ["You control who’s in", "Invite relatives with one-time links; remove access any time."],
                ["Optional public link", "Turn on a read-only share link only when you choose to."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#007F6B]/12 text-[#007F6B]"><ShieldCheck size={15} /></span>
                  <div>
                    <div className="font-semibold text-[#082D4A]">{t}</div>
                    <div className="text-sm text-[#475569]">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#082D4A] px-8 py-14 text-center sm:px-16">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#D4A017]/20 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-[#007F6B]/20 blur-2xl" />
          <h2 className="relative font-serif text-3xl font-bold text-white sm:text-4xl">Start preserving your family’s story today</h2>
          <p className="relative mx-auto mt-3 max-w-xl text-base text-white/80">Create your own private family tree and turn memories into something the whole family can treasure.</p>
          <Button onClick={onSignUp} data-testid="final-cta-signup-btn" className="relative mt-8 h-12 rounded-full bg-[#D4A017] px-8 text-base font-semibold text-[#082D4A] transition-transform hover:-translate-y-0.5 hover:bg-[#B0850F]">
            Create your family tree <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Brand lockup band */}
      <section className="border-t border-[#D8E0E8] bg-[#F4F6F8]">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 py-14 text-center">
          <div data-testid="brand-lockup" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="" className="h-12 w-12 object-contain sm:h-16 sm:w-16" />
            <span className="font-brand text-3xl tracking-tight text-[#0B2F4A] sm:text-4xl">Where Am i From<span className="text-[#8A6A0C]">.net</span></span>
          </div>
          <p className="max-w-md text-sm text-[#475569]">Family. Heritage. Identity. Connection. One story that belongs to all of you.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#D8E0E8] bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="" className="h-9 w-9 object-contain" />
            <span className="font-brand text-xl text-[#0B2F4A]">Where Am i From<span className="text-[#8A6A0C]">.net</span></span>
          </div>
          <div className="flex flex-col items-center gap-2 sm:items-end">
            <button onClick={onDonate} data-testid="footer-donate-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8A6A0C] transition-colors hover:text-[#B0850F] hover:underline">
              <Heart size={14} /> Support this project
            </button>
            <p className="text-xs text-[#475569]">© {new Date().getFullYear()} Where Am i From · Discover the roots of your story</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
